/**
 * Conversation URL Helper Utilities
 * Provides secure URL generation and resolution for conversations
 * Uses participant-based URLs instead of exposing conversation IDs
 */

import { supabase } from './supabase';

/**
 * Generate secure conversation URL using participant info
 * @param {Object} otherUser - The other participant's user info
 * @param {string} jobId - Optional job ID for job-related conversations
 * @returns {string} Secure conversation URL
 */
export const getConversationUrl = (otherUser, jobId = null) => {
  const baseUrl = '/messages/with';
  const userSlug = createUserSlug(otherUser);
  
  if (jobId) {
    return `${baseUrl}/${userSlug}?job=${jobId}`;
  }
  
  return `${baseUrl}/${userSlug}`;
};

/**
 * Create a URL-safe slug from user info
 * Combines name with ID hash for uniqueness
 * @param {Object} user - User object with id and name information
 * @returns {string} URL-safe slug
 */
export const createUserSlug = (user) => {
  if (!user || !user.id) {
    throw new Error('User object must have an id property');
  }

  // Get name from user object, fallback to generic names if not available
  const name = user.full_name || user.company_name || 'user';
  
  // Create URL-safe name portion of the slug
  const namePart = name.toLowerCase()
    .replace(/[^a-z0-9\s-]/g, '') // Remove special characters except spaces and hyphens
    .replace(/\s+/g, '-')         // Replace spaces with hyphens
    .replace(/-+/g, '-')          // Replace multiple hyphens with single hyphen
    .trim('-')                    // Remove leading/trailing hyphens
    .slice(0, 40);                // Limit length of name part
  
  // Extract the last 8 characters of the UUID (without hyphens)
  // Handle both string UUIDs and UUIDs with hyphens
  let idHash = '';
  try {
    // Convert to string if it's not already
    const idStr = String(user.id);
    // Remove hyphens from UUID
    const cleanId = idStr.replace(/-/g, '');
    // Use last 8 characters of the cleaned ID
    idHash = cleanId.slice(-8).toLowerCase();
    
    // Validate that we got a valid hash (8 hex characters)
    if (!idHash || idHash.length !== 8 || !/^[0-9a-f]{8}$/i.test(idHash)) {
      // If we didn't get a valid hash, use a fallback
      console.warn('Failed to extract valid ID hash from user ID:', idStr);
      // Use first 8 characters or any other part that is valid hex
      const hexMatch = cleanId.match(/[0-9a-f]{8}/i);
      if (hexMatch) {
        idHash = hexMatch[0].toLowerCase();
      } else {
        // Last resort - create a hash from the string
        idHash = (Math.abs(idStr.split('').reduce((a, b) => {
          return ((a << 5) - a) + b.charCodeAt(0) | 0;
        }, 0)) % 0xFFFFFFFF).toString(16).padStart(8, '0');
      }
    }
  } catch (err) {
    console.error('Error creating ID hash for slug:', err);
    // Create a fallback hash based on timestamp
    idHash = Date.now().toString(16).slice(-8);
  }
  
  return `${namePart}-${idHash}`;
};

/**
 * Extract user ID hash from slug
 * @param {string} slug - The participant slug from URL
 * @returns {string|null} User ID hash or null if invalid
 */
export const extractUserIdFromSlug = (slug) => {
  if (!slug || typeof slug !== 'string') {
    console.warn('Invalid slug provided to extractUserIdFromSlug:', slug);
    return null;
  }

  // Extract the last segment from the slug
  const parts = slug.split('-');
  const lastPart = parts[parts.length - 1];
  
  // Validate that it's a valid ID hash (8 hex characters)
  if (lastPart && lastPart.length === 8 && /^[0-9a-f]{8}$/i.test(lastPart)) {
    return lastPart.toLowerCase();
  }
  
  // If no valid hash in last part, try to find a valid hash pattern anywhere in the slug
  const hashMatch = slug.match(/[0-9a-f]{8}$/i);
  if (hashMatch) {
    return hashMatch[0].toLowerCase();
  }
  
  console.warn('Failed to extract valid ID hash from slug:', slug, 'Parts:', parts);
  return null;
};

/**
 * Find user by ID hash
 * @param {string} idHash - Last 8 characters of user ID
 * @returns {Object} User object or null
 */
export const findUserByIdHash = async (idHash) => {
  try {
    if (!idHash || idHash.length !== 8) {
      return { success: false, error: 'Invalid ID hash' };
    }

    // Use a SQL function to find users with ID ending in the given hash
    // This performs the string operation in PostgreSQL rather than using LIKE on UUID
    const { data, error } = await supabase.rpc('find_user_by_id_hash', {
      p_id_hash: idHash
    });

    if (error) {
      console.error('Error calling find_user_by_id_hash RPC:', error);
      
      // If the RPC doesn't exist or fails, fall back to client-side filtering
      console.warn('RPC function failed, using client-side filtering');
      
      const { data: users, error: usersError } = await supabase
        .from('user_profiles')
        .select('id, full_name, company_name, role, profile_picture');
        
      if (usersError) throw usersError;
      
      if (!users || users.length === 0) {
        return { success: false, error: 'No users found' };
      }
      
      // Client-side filtering for the ID hash
      const matchingUser = users.find(user => {
        if (!user.id) return false;
        const cleanId = user.id.replace(/-/g, '');
        return cleanId.toLowerCase().endsWith(idHash.toLowerCase());
      });
      
      if (!matchingUser) {
        return { success: false, error: 'User not found with the given ID hash' };
      }
      
      return { success: true, user: matchingUser };
    }

    if (!data || data.length === 0) {
      return { success: false, error: 'User not found with the given ID hash' };
    }
    
    return { success: true, user: data[0] };

  } catch (error) {
    console.error('Error finding user by ID hash:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Find conversation by participant slug
 * Creates conversation if it doesn't exist
 * @param {string} currentUserId - Current user's ID
 * @param {string} participantSlug - Other participant's slug
 * @param {string} jobId - Optional job ID
 * @returns {Object} Conversation details or error
 */
export const getConversationBySlug = async (currentUserId, participantSlug, jobId = null) => {
  try {
    if (!currentUserId || !participantSlug) {
      throw new Error('Missing required parameters');
    }

    const idHash = extractUserIdFromSlug(participantSlug);
    if (!idHash) {
      throw new Error('Invalid participant slug format');
    }

    // Find the other user
    const userResult = await findUserByIdHash(idHash);
    if (!userResult.success) {
      throw new Error(userResult.error || 'Participant not found');
    }

    const participant = userResult.user;

    // Prevent self-messaging
    if (participant.id === currentUserId) {
      throw new Error('Cannot start conversation with yourself');
    }

    // Find or create conversation using the stored procedure
    const { data: conversation, error: convError } = await supabase
      .rpc('create_or_get_conversation', {
        user1_id: currentUserId,
        user2_id: participant.id,
        p_job_id: jobId
      });

    if (convError) {
      throw convError;
    }

    // Handle different return formats from the RPC function
    let conversationId;
    if (Array.isArray(conversation)) {
      conversationId = conversation[0]?.conversation_id || conversation[0]?.id;
    } else {
      conversationId = conversation?.conversation_id || conversation?.id;
    }

    if (!conversationId) {
      throw new Error('Failed to create or retrieve conversation');
    }

    return {
      success: true,
      conversationId,
      participant
    };

  } catch (error) {
    console.error('Error getting conversation by slug:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Validate conversation access
 * Ensures user has permission to access the conversation
 * @param {string} conversationId - Conversation ID
 * @param {string} userId - User ID to check access for
 * @returns {Object} Access validation result
 */
export const validateConversationAccess = async (conversationId, userId) => {
  try {
    if (!conversationId || !userId) {
      return { hasAccess: false, error: 'Missing parameters' };
    }

    const { data, error } = await supabase
      .from('conversations')
      .select('participant_1, participant_2')
      .eq('id', conversationId)
      .single();

    if (error) {
      return { hasAccess: false, error: error.message };
    }

    const hasAccess = data.participant_1 === userId || data.participant_2 === userId;
    
    return { hasAccess, error: null };

  } catch (error) {
    console.error('Error validating conversation access:', error);
    return { hasAccess: false, error: error.message };
  }
};

/**
 * Get conversation participants info
 * @param {string} conversationId - Conversation ID
 * @returns {Object} Participants information
 */
export const getConversationParticipants = async (conversationId) => {
  try {
    const { data, error } = await supabase
      .from('conversations')
      .select(`
        participant_1,
        participant_2,
        participant_1_profile:user_profiles!conversations_participant_1_fkey(
          id, full_name, company_name, role, profile_picture
        ),
        participant_2_profile:user_profiles!conversations_participant_2_fkey(
          id, full_name, company_name, role, profile_picture
        )
      `)
      .eq('id', conversationId)
      .single();

    if (error) {
      throw error;
    }

    return {
      success: true,
      participants: {
        participant_1: data.participant_1_profile,
        participant_2: data.participant_2_profile
      }
    };

  } catch (error) {
    console.error('Error getting conversation participants:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Generate conversation URL from existing conversation data
 * @param {Object} conversation - Conversation object
 * @param {string} currentUserId - Current user ID
 * @param {string} jobId - Optional job ID
 * @returns {string} Secure conversation URL
 */
export const getUrlFromConversation = (conversation, currentUserId, jobId = null) => {
  try {
    // Determine the other participant
    const otherParticipantId = conversation.participant_1 === currentUserId 
      ? conversation.participant_2 
      : conversation.participant_1;

    // Get the other participant's profile info
    const otherUser = conversation.participant_1 === currentUserId
      ? conversation.participant_2_profile || conversation.other_participant
      : conversation.participant_1_profile || conversation.other_participant;

    if (!otherUser) {
      throw new Error('Cannot determine other participant');
    }

    // Ensure the user object has the correct ID
    const userWithId = {
      ...otherUser,
      id: otherParticipantId
    };

    return getConversationUrl(userWithId, jobId);

  } catch (error) {
    console.error('Error generating URL from conversation:', error);
    return '/messages'; // Fallback to messages list
  }
};

export default {
  getConversationUrl,
  createUserSlug,
  extractUserIdFromSlug,
  findUserByIdHash,
  getConversationBySlug,
  validateConversationAccess,
  getConversationParticipants,
  getUrlFromConversation
};